import { type PartialPropsOf } from '@blaze-runtime/engine-domain';

import {
  type ElementLabelWidthProps,
  getClearElementContentOverridesPropsPatchWithSimpleAlign,
  getLabelWidthOverride,
  shouldPatchProp,
} from '../../utils/elements/props-builder.utils';
import { PropsBuilder } from '../__base/props-builder';
import {
  type FormChildrenContentOverrides,
  type FormChildrenStyleOverrides,
} from '../form-container/form-container.model';

export class ImagePropsBuilder extends PropsBuilder<'image'> {
  override setParentContentProps = (
    elementProps: PartialPropsOf<'image'>,
    overrides: FormChildrenContentOverrides | null,
    initialValues?: PartialPropsOf<'image'>,
  ): this => {
    if (overrides === null) {
      this.patchProps(getClearElementContentOverridesPropsPatchWithSimpleAlign());
      return this;
    }

    if (
      shouldPatchProp(
        overrides.label?.position,
        'content.title.position',
        elementProps,
        initialValues,
      )
    ) {
      this.patchProps({ content: { title: { position: overrides.label.position } } });
    }

    if (
      shouldPatchProp(overrides.label?.align, 'content.title.align', elementProps, initialValues)
    ) {
      this.patchProps({ content: { title: { align: overrides.label.align } } });
    }

    const titlePatch = getLabelWidthOverride(overrides, elementProps as ElementLabelWidthProps);
    if (titlePatch) {
      this.patchProps(titlePatch);
    }

    return this;
  };

  override setParentStyleProps = (
    elementProps: PartialPropsOf<'image'>,
    overrides: FormChildrenStyleOverrides | null,
    initialValues?: PartialPropsOf<'image'>,
  ): this => {
    if (overrides === null) {
      this.patchProps({
        style: {
          general: undefined,
          outline: undefined,
        },
      });
      return this;
    }

    const { titleLabel, fieldBox } = overrides;

    if (shouldPatchProp(titleLabel?.color, 'style.general.title', elementProps, initialValues)) {
      this.patchProps({ style: { general: { title: titleLabel.color } } });
    }

    if (shouldPatchProp(fieldBox?.outline, 'style.outline.color', elementProps, initialValues)) {
      this.patchProps({ style: { outline: { color: fieldBox.outline } } });
    }
    if (
      shouldPatchProp(fieldBox?.outlineWidth, 'style.outline.width', elementProps, initialValues)
    ) {
      this.patchProps({ style: { outline: { width: fieldBox.outlineWidth } } });
    }
    if (shouldPatchProp(fieldBox?.radius, 'style.outline.radius', elementProps, initialValues)) {
      this.patchProps({ style: { outline: { radius: fieldBox.radius } } });
    }

    return this;
  };

  constructor(props: PartialPropsOf<'image'> | undefined) {
    super(props, { hasData: true, hasLabel: true });
  }
}
